import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, Clock, Loader2, Send } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { BRAND } from "@/data/catalog";
import { submitContact } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitContact(form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const cards = [
    { Icon: Phone, t: "Call Us", v: BRAND.phone, href: `tel:${BRAND.phone.replace(/\s/g, "")}` },
    { Icon: Mail, t: "Email Us", v: BRAND.email, href: `mailto:${BRAND.email}` },
    { Icon: MessageCircle, t: "WhatsApp", v: "Chat with us", href: `https://wa.me/${BRAND.whatsapp}` },
    { Icon: Clock, t: "Business Hours", v: BRAND.hours },
  ];
  const inputCls = "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-royal";

  return (
    <div data-testid="contact-page">
      <PageHeader overline="Get In Touch" title="Let's talk packaging." subtitle="Have a requirement or a question? Reach out and our team will respond within one business day." />

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10 grid lg:grid-cols-2 gap-12 items-start">
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            {cards.map((c) => {
              const inner = (
                <div className="rounded-3xl border border-border bg-card p-6 h-full hover:border-royal/40 transition-colors">
                  <span className="grid place-items-center h-11 w-11 rounded-2xl bg-secondary text-royal"><c.Icon size={20} /></span>
                  <h3 className="mt-4 font-heading font-bold text-foreground">{c.t}</h3>
                  <p className="mt-1 text-sm text-muted-foreground break-words">{c.v}</p>
                </div>
              );
              return c.href ? <a key={c.t} href={c.href} target="_blank" rel="noreferrer" data-testid={`contact-${c.t.toLowerCase().replace(/\s/g, "-")}`}>{inner}</a> : <div key={c.t}>{inner}</div>;
            })}
          </div>
          <div className="rounded-3xl border border-border bg-card p-6 flex items-start gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-2xl bg-secondary text-royal shrink-0"><MapPin size={20} /></span>
            <div><h3 className="font-heading font-bold text-foreground">Visit Us</h3><p className="mt-1 text-sm text-muted-foreground">{BRAND.address}</p></div>
          </div>
          <div className="overflow-hidden rounded-3xl border border-border">
            <iframe title="ElitzPacks location" data-testid="contact-map" width="100%" height="280" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" src="https://www.google.com/maps?q=Peenya%20Industrial%20Area%20Bengaluru&output=embed" />
          </div>
        </div>

        <Reveal>
          <form onSubmit={submit} data-testid="contact-form" className="rounded-4xl border border-border bg-card p-8 space-y-4">
            <h2 className="font-heading text-2xl font-extrabold text-foreground">Send an enquiry</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name" />
              <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone" />
              <input placeholder="Subject" value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject" />
            </div>
            <textarea required rows={5} placeholder="Your message *" value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message" />
            <button type="submit" disabled={busy} data-testid="contact-submit" className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy text-white px-7 py-4 font-semibold hover:bg-royal transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <>Send Message <Send size={16} /></>}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
